import { DeviceStatus } from "../results/results-view/ResultsView.types";
import {
  DeviceTesterTestState,
  DeviceInfo,
  DeviceSettings,
  AudioTestState,
  AudioDevices,
  CameraDevices,
  DeviceStatusDialogState,
  ResultsViewState,
} from "../../types/Device.types";

import {
  DeviceTesterAction,
  DeviceTesterActionType,
  DeviceTesterState,
} from "./Reducer.types";

export const DeviceTesterReducer = (
  state: DeviceTesterState,
  action: DeviceTesterAction
): DeviceTesterState => {
  switch (action.type) {
    case DeviceTesterActionType.SET_DEVICE_TEST_STATE:
      return {
        ...state,
        deviceTesterTestState: action.state,
        audioTestState: AudioTestState.INITIATED,
        resultsViewState: ResultsViewState.INITIAL,
        deviceStatusDialogState: DeviceStatusDialogState.CLOSED,
      };

    case DeviceTesterActionType.SET_AUDIO_TEST_STATE:
      return { ...state, audioTestState: action.audioTestState };

    case DeviceTesterActionType.SET_RESULTS_VIEW_STATE:
      return { ...state, resultsViewState: action.resultsViewState };

    case DeviceTesterActionType.SET_DEVICE_STATUS_DIALOG_STATE:
      return {
        ...state,
        deviceStatusDialogState: action.deviceStatusDialogState,
      };

    case DeviceTesterActionType.SET_DEVICE_INFO:
      return setDeviceInfo(state, action.deviceInfo);

    case DeviceTesterActionType.UPDATE_AUDIO_DEVICES:
      return updateAudioDevices(state, action.newAudioDevices);

    case DeviceTesterActionType.UPDATE_VIDEO_DEVICES:
      return updateVideoDevices(state, action.newCameraDevices);

    case DeviceTesterActionType.SET_CAMERA_RESULT:
      return { ...state, cameraResult: action.cameraResult };

    case DeviceTesterActionType.SET_NETWORK_RESULT:
      return { ...state, networkResult: action.networkResult };

    case DeviceTesterActionType.SET_MICROPHONE_RESULT:
      return { ...state, microphoneResult: action.microphoneResult };

    case DeviceTesterActionType.SET_SPEAKER_RESULT:
      return { ...state, speakerResult: action.speakerResult };

    case DeviceTesterActionType.SET_VIDEO_STREAMING:
      return {
        ...state,
        deviceSettings: { ...state.deviceSettings, isCamOn: action.isCamOn },
      };

    case DeviceTesterActionType.SET_SELECTED_SPEAKER:
      return getSelectedSpeaker(state, action.speakerId);

    case DeviceTesterActionType.SET_SELECTED_MICROPHONE:
      return getSelectedMic(state, action.microphoneId);

    case DeviceTesterActionType.SET_SELECTED_CAMERA:
      return getSelectedCamera(state, action.cameraId);

    case DeviceTesterActionType.RESET_DEVICE_TEST:
      return getResetDeviceTestState(state);
  }
};

// Helper to set the new
const updateAudioDevices = (
  state: DeviceTesterState,
  newAudioDevices: AudioDevices
): DeviceTesterState => {
  return {
    ...state,
    deviceSettings: {
      ...state.deviceSettings,
      speakers: newAudioDevices.speakers,
      microphones: newAudioDevices.microphones,
    },
  };
};

// Helper to udpate the device lists
const updateVideoDevices = (
  state: DeviceTesterState,
  newCameraDevices: CameraDevices
): DeviceTesterState => {
  return {
    ...state,
    deviceSettings: {
      ...state.deviceSettings,
      cameras: newCameraDevices.cameras,
    },
  };
};

// Helper to get the device information from an api call and initialize device test results
// based on device availability
const setDeviceInfo = (
  state: DeviceTesterState,
  deviceInfo: DeviceInfo
): DeviceTesterState => {
  return {
    ...state,
    deviceSettings: {
      isCamOn: deviceInfo.defaultCamera ? true : false,
      selectedCamera:
        state.deviceSettings.selectedCamera ?? deviceInfo.defaultCamera,
      selectedMicrophone:
        state.deviceSettings.selectedMicrophone ?? deviceInfo.defaultMicrophone,
      selectedSpeaker:
        state.deviceSettings.selectedSpeaker ?? deviceInfo.defaultSpeaker,
      speakers: deviceInfo.speakers,
      cameras: deviceInfo.cameras,
      microphones: deviceInfo.microphones,
      cameraAccess: deviceInfo.cameraAccess,
      micAccess: deviceInfo.micAccess,
    },
    isInfoFetched: true,
    isAcsTokenFetched: deviceInfo.isAcsTokenFetched,
  };
};

// Helper to assign the selected microphone device based on an id
const getSelectedMic = (
  state: DeviceTesterState,
  microphoneId: string | null
): DeviceTesterState => {
  const deviceSettings: DeviceSettings = state.deviceSettings;
  if (microphoneId && deviceSettings.microphones) {
    const selectedMicDetails = deviceSettings.microphones.filter(
      (mic) => mic.id === microphoneId
    );
    if (selectedMicDetails?.length) {
      return {
        ...state,
        deviceSettings: {
          ...deviceSettings,
          selectedMicrophone: selectedMicDetails[0],
        },
      };
    }
  }
  return state;
};

// Helper to assign the selected speaker device based on an id
const getSelectedSpeaker = (
  state: DeviceTesterState,
  speakerId: string | null
): DeviceTesterState => {
  const deviceSettings: DeviceSettings = state.deviceSettings;
  if (speakerId && deviceSettings.speakers) {
    const selectedSpeakerDetails = deviceSettings.speakers.filter(
      (speaker) => speaker.id === speakerId
    );
    if (selectedSpeakerDetails?.length) {
      return {
        ...state,
        deviceSettings: {
          ...deviceSettings,
          selectedSpeaker: selectedSpeakerDetails[0],
        },
      };
    }
  }
  return state;
};

// Helper to assign the selected camera device based on an id
const getSelectedCamera = (
  state: DeviceTesterState,
  cameraId: string | null
): DeviceTesterState => {
  const deviceSettings: DeviceSettings = state.deviceSettings;
  if (cameraId && deviceSettings.cameras) {
    const selectedCameraDetails = deviceSettings.cameras.filter(
      (camera) => camera.id === cameraId
    );
    if (selectedCameraDetails?.length) {
      return {
        ...state,
        deviceSettings: {
          ...deviceSettings,
          selectedCamera: selectedCameraDetails[0],
        },
      };
    }
  }
  return state;
};

// Helper to assign the initial state for device test results
const getResetDeviceTestState = (
  state: DeviceTesterState
): DeviceTesterState => {
  return {
    ...state,
    deviceTesterTestState: DeviceTesterTestState.RESULTS,
    cameraResult: { status: DeviceStatus.UNTESTED },
    microphoneResult: { status: DeviceStatus.UNTESTED },
    speakerResult: { status: DeviceStatus.UNTESTED },
    networkResult: { status: DeviceStatus.UNTESTED },
  };
};
